import { Meteor } from 'meteor/meteor';
import styled from 'styled-components';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Accounts } from 'meteor/accounts-base';
import { strings } from '/imports/strings';
import { ErrorCode } from '/imports/errors';
import { defer, isMobile, isValidEmail, localStorageSet, sleep } from '/imports/utils';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { PublicFooter } from '/imports/ui/PublicFooter';
import { Loading } from '/imports/ui/Loading';
import { SignedOutPageStyled } from '/imports/ui/SignedOutPage';
import { Logo } from '/imports/ui/Logo';
import { toastOnError } from '/imports/ui/utils';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import { sendLoginLink } from '/imports/api/users';
import { Checkbox } from '/imports/ui/Checkbox';

export const LoginPageStyled = styled(SignedOutPageStyled)`
  .logo {
    max-width: 100%;
    img {
      width: 100%;
    }
  }
  .links {
    position: relative;
  }
  .magic-link {
    position: absolute;
    left: 0;
  }
  .forgot-password {
    position: absolute;
    right: 0;
  }
  .signup {
    display: none;
    margin-top: 2rem;
    text-align: center;
    p {
      margin: 0;
    }
    .links {
      display: flex;
      gap: 2.5rem;
      justify-content: center;
    }
  }
  .inputs {
    label {
      margin-top: 1rem;
    }
  }
  /* Above the Sign In button, which it enables: a reader fills the form, ticks, and the next thing down
     is the button. Below it, people reached for a disabled button first. */
  .terms {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin: 1.5rem 0 1rem;
    text-align: left;
    font-size: 0.9rem;
    input[type="checkbox"] {
      margin-top: 0.2rem;
    }
  }
`;

const defaultRedirect = '/';

/** Login page supporting password sign-in, magic-link, JWT auto-login, and terms acceptance. */
export const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const redirect = window.atob(params.get('r') || '') || defaultRedirect;
  const [jwt, setJwt] = useState(params.get('jwt'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [enableLink, setEnableLink] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  useEffect(() => { localStorageSet('returnVisitor', true); }, []);
  useEffect(() => {
    isValidEmail(username) ? setEnableLink(false) : setEnableLink(false);
  }, [username]);
  useEffect(() => {
    if (!jwt) return;
    Accounts.callLoginMethod({
      methodArguments: [{ jwt }], userCallback(error) {
        if (error) {
          toast.error(strings.InvalidOrExpiredVerificationToken());
          setLoggingIn(false);
          setJwt('');
        }
        else {
          navigate('/');
        }
      },
    });
  }, [jwt]);
  /**
   * Handles form submit: password login when both fields present, otherwise sends a magic link.
   * @param e - The form submit event.
   */
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLoggingIn(true);
    setError('');
    if (!agreedTerms) return;
    if (username && password) login();
    if (username && !password) magicLink();
  };
  /** Logs in with username/password, mapping error codes to messages, then redirects once the user resolves. */
  const login = () => {
    Meteor.loginWithPassword({ username }, password, (error: any) => {
      setLoggingIn(false);
      if (error?.error === 403) {
        setError(strings.InvalidUsernameOrPassword());
        return false;
      }
      else if (error?.error === ErrorCode.unauthorized) {
        setError(strings.InvalidUsernameOrPassword());
        return false;
      }
      else if (error?.error === ErrorCode.inactiveUserLogin) {
        setError(strings.AccountIsDeactivated());
        return false;
      }
      else if (error?.error === ErrorCode.unverifiedUserLogin) {
        setError(strings.PleaseVerifyYourEmailAddressBeforeLoggingIn());
        return false;
      }
      else if (error) {
        console.log(error);
      }
      else {
        setLoggingIn(true);
        defer(async () => {
          // `/` forwards to the user's landing page (see LandingRedirect); an explicit `r` wins so a
          // session that expired mid-page resumes there.
          while (!await Meteor.userAsync()) await sleep(250);
          window.location.href = redirect.includes('logout') ? defaultRedirect : redirect;
        });
      }
    });
  };
  /** Sends a passwordless login link to the entered email and toasts confirmation. */
  const magicLink = toastOnError(async () => {
    const email = await sendLoginLink.call(username);
    setLoggingIn(false);
    toast.success(strings.LinkSentPleaseCheckYourInbox(email));
  });
  if (jwt) return <LoadingFullScreen />;
  return (
    <LoginPageStyled className="page">
      <div className="wrapper">
        <div className="inner-wrapper">
          <div><Logo size={isMobile() ? 3 : 5} /></div>
          <div>
            <div>{strings.PleaseEnterYourLoginDetailsBelow()}</div>
          </div>
          <form onSubmit={onSubmit}>
            <div className="inputs">
              <label>{strings.Email()}</label>
              <Input inputId="login-username" placeholder="mail@website.com" value={username} autoFocus={true}
                onChange={(username) => setUsername(username || '')} />
              <label>{strings.Password()}</label>
              <Input inputId="login-password" type="password" placeholder="••••••••••••" value={password}
                onChange={(password) => setPassword(password || '')} autoComplete='off' onFocus={(e) => e.target.removeAttribute('readonly')} />
              {error && <div className="form-error">{error}</div>}
              <div className="links">
                <Link className="forgot-password" to='/forgot-password'>{strings.ForgotPassword()}</Link>
                {enableLink && <Link className="magic-link" to='#' onClick={() => magicLink()}>{strings.SendLoginLink()}</Link>}
              </div>
            </div>
            <div className="terms">
              <Checkbox checked={agreedTerms} onChange={(checked) => setAgreedTerms(checked)} />
              <span>
                {strings.TermsAcknowledgement()}{' '}
                <a href="https://luminia.io/terms-of-service/" target="_blank" rel="noreferrer">{strings.TermsOfServiceLink()}</a>
                {` ${strings.And()} `}
                <a href="https://luminia.io/privacy-policy/" target="_blank" rel="noreferrer">{strings.PrivacyPolicy()}</a>
              </span>
            </div>
            <div className="buttons">
              <Button
                id="login-submit" type="submit"
                className={`btn ${loggingIn ? 'tertiary' : 'primary'}`}
                disabled={!username || !agreedTerms}
              >
                {
                  loggingIn ?
                    <Loading />
                    :
                    <div className="button-label">{username && !password ? strings.SendLoginLink() : strings.SignIn()}</div>
                }
              </Button>
              <div className="signup">
                <p>{strings.DontHaveAnAccount()}</p>
                <p className="links"><Link to="/signup">{strings.MakeOneHere()}</Link></p>
              </div>
            </div>
          </form>
        </div>
      </div>
      <PublicFooter />
    </LoginPageStyled>
  );
};